import type React from 'react'
import ModuleContext, { type ModuleContextType } from '@pages/activityBuilder/ModuleContext'
import { useLocation } from 'react-router-dom'
import { useContextAwareGetLandUseChangeByActivityId } from '@hooks/index'
import { useEffect, useMemo, useState } from 'react'
import { useAppParams } from '@app/hooks'
import type { BelongsToLuc } from '@typedefs/modulesInterfaces'
import { BEModulesClassNames, getModuleFromUrl, isLandUseModuleByClassName } from '@utils/moduleList'
import { ForestProvider } from '@contexts/ForestContext'
import { SettlementProvider } from '@contexts/SettlementProvider'
import { useModuleTypeRegistry } from '@utils/useModuleTypeRegistry'
import EnergyProvider from '@contexts/EnergyProvider'
import IrrigationsProvider from '@contexts/IrrigationsProvider'
import InputsProvider from '@contexts/InputsProvider'

interface ProviderProps {
    children: React.ReactNode
}

const Provider: React.FC<ProviderProps> = (
    { children }
) => {
    const { activityId } = useAppParams();
    const location = useLocation();
    const [belongsToLuc, setBelongsToLuc] = useState<BelongsToLuc | undefined>(undefined);
    
    // Initialize the registry and get its state
    const { registry, isLoading: isRegistryLoading } = useModuleTypeRegistry();
    
    const FEModule = useMemo(() => getModuleFromUrl(location.pathname), [location]);

    // Use class name for comparison instead of numeric ID
    const moduleClassName = FEModule?.beModuleClassName;

    const isLUCModule = useMemo(() => {
        if (!moduleClassName) return false;
        return isLandUseModuleByClassName(moduleClassName);
    }, [moduleClassName]);

    const { data: lucData } = useContextAwareGetLandUseChangeByActivityId({ activityId: activityId ? Number.parseInt(activityId) : 0 }, {
        refetchOnMountOrArgChange: true,
        skip: !activityId || activityId === '0',
    });

    useEffect(() => {
        if (!isRegistryLoading && lucData && moduleClassName !== BEModulesClassNames.LandUseChange && isLUCModule) {
            const lucStartClassName = registry.getClassNameById(lucData.module.module_type_start ?? 0);
            const lucWClassName = registry.getClassNameById(lucData.module.module_type_w ?? 0);
            const lucWoClassName = registry.getClassNameById(lucData.module.module_type_wo ?? 0);

            setBelongsToLuc({
                is_start: moduleClassName === lucStartClassName,
                is_w: moduleClassName === lucWClassName,
                is_wo: moduleClassName === lucWoClassName,
            });
        } else if (!isRegistryLoading && (!lucData || !isLUCModule)) {
            setBelongsToLuc(undefined);
        }
    }, [lucData, moduleClassName, activityId, isLUCModule, isRegistryLoading, registry]);

    const context: ModuleContextType = {
        belongsToLuc
    }

    return (
        <ModuleContext.Provider value={context}>
            {
                moduleClassName === BEModulesClassNames.ForestManagement ? (
                    <ForestProvider>
                        {children}
                    </ForestProvider>
                ) : moduleClassName === BEModulesClassNames.Settlements ? (
                    <SettlementProvider>
                        {children}
                    </SettlementProvider>
                ) : moduleClassName === BEModulesClassNames.Energy ? (
                    <EnergyProvider>
                        {children}
                    </EnergyProvider>
                ) : moduleClassName === BEModulesClassNames.Irrigation ? (
                    <IrrigationsProvider>
                        {children}
                    </IrrigationsProvider>
                ) : moduleClassName === BEModulesClassNames.Inputs ? (
                    <InputsProvider>
                        {children}
                    </InputsProvider>
                ) : children
            }
        </ModuleContext.Provider>
    )
}

export default Provider